/**
 * KioskReviewStep — a VIEW-ONLY disclosure presented on the signer kiosk /
 * remote signer screen (Bill 2026-09-30). Rendered by SignerKiosk when the
 * current step has stepKind === 'review'.
 *
 * Same shape as KioskConsentStep: the document is rendered (KioskDocumentViewer,
 * pdf.js), a progress bar tracks how far the signer has scrolled, and the
 * acknowledgement button enables at >= 80%. Nothing is signed. The POST goes to
 * the ordinary kiosk capture endpoint with captureMode 'review' and the exact
 * acknowledgement sentence the signer accepted; the server records a
 * 'disclosure-reviewed' event, never a signature row.
 *
 * Why it exists: view-only disclosures were dropped from the ceremony on
 * 2026-07-02 and archived at completion instead; the Certificate then claimed
 * they had been "presented" although no signer ever saw them. This is the
 * presentation.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { KioskCaptureRequest, KioskCaptureResponse } from '@retail-ops/shared-types';
import { ENV } from '@/constants/env';
import { kioskFetch } from './kioskSession';
import { KioskDocumentViewer } from './KioskDocumentViewer';

export interface KioskReviewStepProps {
  token: string;
  documentId: string;
  docTitle?: string | null;
  signerName?: string | null;
  onSubmittingChange: (submitting: boolean) => void;
  onSaved: () => void;
}

const SCROLL_THRESHOLD_PCT = 80;

// ── styles (kiosk conventions — see KioskConsentStep.tsx) ────────────────
const cardS: CSSProperties = {
  background: '#fff', borderRadius: 12, height: '92vh', width: '100%',
  boxShadow: '0 10px 36px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const headerS: CSSProperties = { padding: '16px 24px 0', flex: '0 0 auto' };
const signerBadgeS: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 1, padding: '8px 14px', marginBottom: 10,
  background: '#eef4fb', border: '1px solid #cfe0f2', borderLeft: '4px solid #2968ad', borderRadius: 8,
};
const signerBadgeLabelS: CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#5b7ba6',
};
const signerBadgeNameS: CSSProperties = { fontSize: 22, fontWeight: 700, color: '#1a406a', lineHeight: 1.2 };
const titleS: CSSProperties = { margin: 0, fontSize: 20, fontWeight: 700, color: '#1a406a' };
const introS: CSSProperties = { margin: '6px 0 0', fontSize: 14, color: '#475569', lineHeight: 1.5 };
const progressWrapS: CSSProperties = { padding: '10px 24px 0', flex: '0 0 auto' };
const progressTrackS: CSSProperties = { height: 6, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' };
const progressFillS = (pct: number, ready: boolean): CSSProperties => ({
  width: `${Math.min(100, pct)}%`, height: '100%',
  background: ready ? '#386732' : '#2968ad',
  transition: 'width 120ms ease-out, background 240ms ease-out',
});
const progressLabelS: CSSProperties = { marginTop: 4, fontSize: 12, color: '#475569' };
const docAreaS: CSSProperties = { flex: '1 1 auto', minHeight: 0, display: 'flex', padding: '10px 24px 0' };
const errorS: CSSProperties = {
  background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
  borderRadius: 8, padding: '10px 14px', fontSize: 14, margin: '10px 24px 0',
};
const footerS: CSSProperties = {
  padding: '14px 24px 18px', flex: '0 0 auto', display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', gap: 12, flexWrap: 'wrap',
};
const ackTextS: CSSProperties = { fontSize: 13, color: '#1f2937', lineHeight: 1.4, flex: '1 1 320px' };
const agreeBtnS = (enabled: boolean): CSSProperties => ({
  padding: '14px 22px', borderRadius: 8, fontSize: 16, fontWeight: 600, color: '#fff',
  background: enabled ? '#386732' : '#9ca3af',
  border: `1px solid ${enabled ? '#386732' : '#9ca3af'}`,
  cursor: enabled ? 'pointer' : 'not-allowed', flex: '0 0 auto',
});

export function KioskReviewStep(props: KioskReviewStepProps): JSX.Element {
  const { token, documentId, docTitle, signerName, onSubmittingChange, onSaved } = props;
  const title = docTitle ?? 'this disclosure';

  const [scrollPct, setScrollPct] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Intent = when this disclosure first came up on the screen.
  const intentTimestampRef = useRef<string>(new Date().toISOString());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // New document → start over: scroll evidence and intent belong to THIS disclosure.
  useEffect(() => {
    setScrollPct(0);
    setError(null);
    intentTimestampRef.current = new Date().toISOString();
  }, [documentId]);

  // Monotonic max — scrolling back up never lowers the evidence.
  const handleScrollProgress = useCallback((pct: number) => {
    setScrollPct((prev) => Math.max(prev, Math.min(100, Math.round(pct))));
  }, []);

  const canAcknowledge = scrollPct >= SCROLL_THRESHOLD_PCT && !submitting;

  const acknowledgement =
    `I, ${signerName ?? 'the undersigned'}, confirm that ${title} was presented to me and I have received and reviewed it.`;

  const handleAcknowledge = useCallback(async () => {
    if (!canAcknowledge) return;
    setSubmitting(true);
    setError(null);
    onSubmittingChange(true);
    try {
      // Server rule: intent must precede the acknowledgement by >= 1s.
      const gap = Date.now() - new Date(intentTimestampRef.current).getTime();
      if (gap < 1100) await new Promise((res) => window.setTimeout(res, 1100 - gap));
      const body: KioskCaptureRequest = {
        token,
        captureMode: 'review',
        clickAttestation: acknowledgement,
        intentTimestamp: intentTimestampRef.current,
        scrollProgressPct: scrollPct,
        signerViewportSize: `${window.innerWidth}x${window.innerHeight}`,
        signerUserAgent: navigator.userAgent,
      };
      const r = await kioskFetch(`${ENV.apiBaseUrl}/v3/signing/kiosk/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = (await r.json()) as KioskCaptureResponse;
      if (!mountedRef.current) return;
      if (j.success) {
        onSaved();
        return;
      }
      if (j.errorCode === 'PRESENTATION_INVALID') {
        setError('This signing session is no longer active. Please ask your banker to reconnect this screen.');
      } else {
        setError(j.error ?? 'We could not record your acknowledgement. Please try again.');
      }
    } catch {
      if (!mountedRef.current) return;
      setError('Connection problem — please try again in a moment.');
    } finally {
      if (mountedRef.current) setSubmitting(false);
      onSubmittingChange(false);
    }
  }, [canAcknowledge, token, acknowledgement, scrollPct, onSubmittingChange, onSaved]);

  return (
    <div style={cardS}>
      <div style={headerS}>
        {signerName && (
          <div style={signerBadgeS}>
            <span style={signerBadgeLabelS}>Reviewing as</span>
            <span style={signerBadgeNameS}>{signerName}</span>
          </div>
        )}
        <h1 style={titleS}>Please review: {title}</h1>
        <p style={introS}>
          This disclosure is provided for your records — no signature is needed. Read through it,
          then tap the button to confirm you received it.
        </p>
      </div>

      <div style={progressWrapS}>
        <div style={progressTrackS}>
          <div style={progressFillS(scrollPct, scrollPct >= SCROLL_THRESHOLD_PCT)} />
        </div>
        <div style={progressLabelS}>
          {scrollPct >= SCROLL_THRESHOLD_PCT
            ? 'Thanks — you have read enough to continue.'
            : `${scrollPct}% read — scroll to the bottom to enable the button`}
        </div>
      </div>

      <div style={docAreaS}>
        <KioskDocumentViewer
          token={token}
          documentId={documentId}
          docTitle={docTitle}
          showTitle={false}
          onScrollProgress={handleScrollProgress}
        />
      </div>

      {error && <div role="alert" style={errorS}>{error}</div>}

      <div style={footerS}>
        <span style={ackTextS}>{acknowledgement}</span>
        <button
          type="button"
          style={agreeBtnS(canAcknowledge)}
          disabled={!canAcknowledge}
          onClick={() => void handleAcknowledge()}
        >
          {submitting ? 'Saving…' : 'I have received and reviewed this disclosure'}
        </button>
      </div>
    </div>
  );
}

export default KioskReviewStep;
